import { useId, type CSSProperties } from 'react'
import { pictureUrl } from '@/lib/cookbooks'
import type { Mark } from '@/lib/print/kit'

// A printed page drawn on screen from the marks the PDF's own drawing code left (print/kit.ts): the same words at the
// same places in the same fonts, the same rules and the same photos cropped the same way. Measured in points over the
// trimmed page, so it keeps its layout at any size; anything set out into the bleed is cut off at the trim, as the
// printer cuts it.
export function MarkedPage({
  marks,
  trim,
  paper = 'white',
}: {
  marks: Mark[]
  trim: { width: number; height: number }
  paper?: 'white' | 'natural'
}) {
  // A clip's id is used in url(), so it's kept to plain characters.
  const id = useId().replace(/[^\w-]/g, '')
  const width = trim.width * 72
  const height = trim.height * 72
  return (
    <div className="kp-page" style={paper === 'natural' ? { background: 'var(--sheet-natural)' } : undefined}>
      <svg viewBox={`0 0 ${width} ${height}`} className="kp-marks" aria-hidden>
        {marks.map((mark, i) => {
          switch (mark.kind) {
            case 'fill':
              return <rect key={i} x={mark.x} y={mark.y} width={mark.w} height={mark.h} style={{ fill: mark.color }} />
            case 'line':
              return (
                <line
                  key={i}
                  x1={mark.x1}
                  y1={mark.y1}
                  x2={mark.x2}
                  y2={mark.y2}
                  strokeWidth={mark.thickness}
                  style={{ stroke: mark.color }}
                />
              )
            case 'text':
              return (
                <text
                  key={i}
                  x={mark.x}
                  y={mark.y}
                  className={mark.face.family === 'serif' ? 'kp-mark-serif' : 'kp-mark-sans'}
                  style={
                    {
                      fontSize: mark.size,
                      fontWeight: mark.face.weight,
                      fontStyle: mark.face.italic ? 'italic' : 'normal',
                      letterSpacing: mark.tracking * mark.size,
                      fill: mark.color,
                    } as CSSProperties
                  }
                >
                  {mark.text}
                </text>
              )
            case 'photo': {
              const clip = mark.shape === 'circle' ? `${id}-${i}` : undefined
              return (
                <g key={i}>
                  {clip && (
                    <clipPath id={clip}>
                      <circle cx={mark.x + mark.w / 2} cy={mark.y + mark.h / 2} r={Math.min(mark.w, mark.h) / 2} />
                    </clipPath>
                  )}
                  <image
                    href={pictureUrl(mark.picture, 'medium')}
                    x={mark.x}
                    y={mark.y}
                    width={mark.w}
                    height={mark.h}
                    preserveAspectRatio="xMidYMid slice"
                    clipPath={clip && `url(#${clip})`}
                  />
                </g>
              )
            }
          }
        })}
      </svg>
    </div>
  )
}
